// @ts-nocheck
/**
 * Toast_Config.tsx — premium themed renderers for react-native-toast-message.
 *
 * Ported verbatim from SK-Hire: a card with an accent rail + icon tile that
 * matches the app, lifted above modals on web. Tokens come from theme/tokens
 * (CSS-var backed, so it auto-themes light/dark on web). `showToast` normalizes
 * tones to success | error | info, so we render exactly those three.
 */
import React from 'react';
import { View, Text, Platform } from 'react-native';
import type { ToastConfig, ToastConfigParams } from 'react-native-toast-message';
import { Colors } from '../../theme/tokens';
import { Icon } from '../Icon';

const isWeb = Platform.OS === 'web';

type Tone = 'success' | 'error' | 'info';

const TONES = {
  success: { fg: Colors.success, soft: Colors.successSoft, line: Colors.successLine, icon: 'CheckCircle' },
  error:   { fg: Colors.danger,  soft: Colors.dangerSoft,  line: Colors.dangerLine,  icon: 'Alert' },
  info:    { fg: Colors.info,    soft: Colors.infoSoft,    line: Colors.infoLine,    icon: 'Bell' },
} as const;

function ToastItem({ tone, text1, text2 }: { tone: Tone; text1?: string; text2?: string }) {
  const t = TONES[tone];
  return (
    <View
      style={{
        flexDirection: 'row', alignItems: 'flex-start', gap: 12, width: '92%', maxWidth: 440,
        paddingVertical: 13, paddingHorizontal: 14, borderRadius: 14, overflow: 'hidden',
        backgroundColor: Colors.card, borderWidth: 1, borderColor: Colors.line,
        ...(isWeb
          ? ({ boxShadow: '0 18px 44px -12px rgba(10,10,10,0.45), 0 2px 8px rgba(10,10,10,0.18)', zIndex: 999999 } as any)
          : { elevation: 14, shadowColor: '#0A0A0A', shadowOpacity: 0.3, shadowRadius: 16, shadowOffset: { width: 0, height: 8 } }),
      }}
    >
      {/* accent rail */}
      <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: t.fg }} />
      <View
        style={{
          width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center',
          backgroundColor: t.soft, borderWidth: 1, borderColor: t.line, marginLeft: 2,
        }}
      >
        <Icon name={t.icon} size={16} color={t.fg} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        {!!text1 && (
          <Text style={{ fontSize: 14.5, fontWeight: '700', color: Colors.ink, letterSpacing: -0.1 }}>{text1}</Text>
        )}
        {!!text2 && (
          <Text style={{ fontSize: 13, color: Colors.ink3, marginTop: 2, lineHeight: 13 * 1.45 }}>{text2}</Text>
        )}
      </View>
    </View>
  );
}

export const toastConfig: ToastConfig = {
  success: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="success" text1={text1} text2={text2} />,
  error: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="error" text1={text1} text2={text2} />,
  info: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="info" text1={text1} text2={text2} />,
};
